<template>
    <!--
    The reason that has-scale-transition is set to false is due to an interation
    that may cause the ImageCropper component to calculate incorrect dimensions
    as the modal is actively being resized as it is being shown.
    -->
    <hox-modal :has-scale-transition="false" @close="close">
        <template #header>Resize Settings</template>
        <div
            v-if="!isSingleTemplateResizeSettings || isSingleTemplateWithOwnMediaItem"
            :class="settingsContainerClasses"
        >
            <ResizeSettings
                v-if="templateIds?.[0]"
                v-model:resize-type="resizeType"
                :resize-dimensions="resizeDimensionsByTemplateId[templateIds[0]]"
                @dimensionChange="onInputChange"
            />
        </div>
        <hox-alert v-if="templateIds.length === 0" type="info">
            <template #title>This image is not used by any templates</template>
            <template #content>
                <p>As such it is not possible to show any preview of how the image will be resized.</p>
            </template>
        </hox-alert>
        <ResizeSettingsTemplateSection
            v-for="templateId of templateIds"
            :key="templateId"
            :initial-has-own-resize-settings="isSingleTemplateResizeSettings"
            :group-resize-type="resizeType"
            :group-resize-settings="internalResizeSettings"
            :label="templateLabels[templateId]"
            :preview-image-url="previewImageUrl"
            :require-click-to-enable-image-cropping-zoom="
                !isSingleTemplateResizeSettings && !isSingleTemplateWithOwnMediaItem
            "
            :resize-dimensions="resizeDimensionsByTemplateId[templateId]!"
            :show-label="isGroupResizeSettings"
            :show-template-specific-settings-switch="isGroupResizeSettings"
            :template-id="templateId"
            :template-resize-settings="resizeSettingsByTemplateId[templateId]"
            :manual-dimensions="manualDimensions"
            @removeTemplateResizeSettings="removeTemplateResizeSettings(templateId)"
            @setTemplateResizeSettings="setTemplateResizeSettings(templateId, $event)"
        />
        <template #footer>
            <Button class="resize-settings-modal--button-spacing" @click="close">Cancel</Button>
            <Button class="resize-settings-modal--button-spacing" type="primary" @click="emitResizeSettings">
                Set Resize Settings
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { CoverType, HorizontalAlign, ResizeType, VerticalAlign } from "shared-utils/utils/templateCover";
import getDimensionModifiers from "shared-utils/utils/getDimensionModifiers";
import type { Dimensions, Editable, HTML5MasterTemplate, NestedResizeSettings, ResizeSettings } from "shared-types";

import ResizeSettingsTemplateSection from "@/components/ResizeSettingsTemplateSection.vue";
import ResizeSettingsComponent from "@/components/ResizeSettings.vue";
import { CampaignGetters } from "@/store/modules/campaign";
import { deepClone } from "@/utils";

export default defineComponent({
    name: "ResizeSettingsModal",

    components: {
        ResizeSettingsTemplateSection,
        ResizeSettings: ResizeSettingsComponent
    },

    props: {
        editable: {
            required: true,
            validator: (value: Partial<Editable>) => value.defaultValues !== undefined,
            type: Object as PropType<Editable>
        },

        templateIds: {
            required: true,
            type: Array as PropType<string[]>
        },

        isGroupResizeSettings: {
            type: Boolean
        },

        isSingleTemplateResizeSettings: {
            type: Boolean
        },

        previewImageUrl: {
            required: true,
            type: String
        },

        resizeSettings: {
            type: Object as PropType<NestedResizeSettings | null>
        }
    },

    emits: ["close", "setResizeSettings"],

    data() {
        return {
            internalResizeSettings: {} as NestedResizeSettings,
            manualDimensions: null as Dimensions | null,
            resizeType: ResizeType.Off as (typeof ResizeType)[keyof typeof ResizeType]
        };
    },

    computed: {
        isSingleTemplateWithOwnMediaItem() {
            return !this.isGroupResizeSettings && this.isSingleTemplateResizeSettings;
        },

        masterTemplates() {
            return this.$store.state.campaign.normalized.masterTemplates;
        },

        resizeDimensionsByTemplateId() {
            return this.templateIds.reduce<Record<string, Dimensions>>((acc, templateId) => {
                const defaultValue = this.editable.defaultValues.find(
                    defaultValue => defaultValue.masterTemplateId === templateId
                );

                if (!defaultValue?.dimensions) {
                    return acc;
                }

                const template = this.masterTemplates[templateId];

                if (!template) {
                    return acc;
                }

                const { widthMultiplier, heightMultiplier } = getDimensionModifiers(template as HTML5MasterTemplate);

                acc[templateId] = {
                    width: Math.round(defaultValue.dimensions.width * widthMultiplier),
                    height: Math.round(defaultValue.dimensions.height * heightMultiplier)
                };

                return acc;
            }, {});
        },

        resizeSettingsByTemplateId() {
            return this.internalResizeSettings.resizeSettingsByTemplateId || {};
        },

        templateLabels() {
            return this.$store.getters[CampaignGetters.masterTemplateLabels];
        },

        settingsContainerClasses() {
            const classes = ["resize-settings-modal__settings-container"];
            if (!this.isGroupResizeSettings && this.isSingleTemplateResizeSettings) {
                classes.push("resize-settings-modal__settings-container--no-bottom-border");
            }
            return classes;
        }
    },

    watch: {
        resizeType(newValue) {
            const cover = newValue === ResizeType.Manual ? CoverType.Manual : undefined;

            if (cover === undefined) {
                return;
            }

            this.internalResizeSettings = {
                ...this.internalResizeSettings,
                cover
            };
        }
    },

    created() {
        if (!this.resizeSettings || !Object.keys(this.resizeSettings).length) {
            return;
        }

        if (this.resizeSettings.cover !== undefined) {
            this.resizeType = ResizeType.Manual;

            this.internalResizeSettings = { ...this.resizeSettings };
        } else {
            this.internalResizeSettings = {
                cover: "cover",
                horizontalAlign: HorizontalAlign.Center,
                manualSettings: null,
                verticalAlign: VerticalAlign.Center,
                resizeSettingsByTemplateId: this.resizeSettings.resizeSettingsByTemplateId
            };
        }
    },

    methods: {
        sanitiseResizeSettings(resizeSettings: Partial<NestedResizeSettings>) {
            /*
                We strip away any bits of data that are not relevant to the current
                resize settings to ensure consistent and predicatable behaviour
                when the user comes back to the resize settings after saving.
            */
            const sanitisedResizeSettings = deepClone(resizeSettings);

            if (!("resizeSettingsByTemplateId" in resizeSettings)) {
                return sanitisedResizeSettings;
            }

            const templateIds = Object.keys(sanitisedResizeSettings.resizeSettingsByTemplateId!);

            if (!templateIds.length) {
                delete sanitisedResizeSettings.resizeSettingsByTemplateId;

                return sanitisedResizeSettings;
            }

            sanitisedResizeSettings.resizeSettingsByTemplateId = templateIds.reduce<Record<string, ResizeSettings>>(
                (acc, templateId) => {
                    acc[templateId] = this.sanitiseResizeSettings(
                        sanitisedResizeSettings.resizeSettingsByTemplateId![templateId]!
                    ) as ResizeSettings;

                    return acc;
                },
                {}
            );

            return sanitisedResizeSettings;
        },

        emitResizeSettings() {
            const hasTemplateSpecificResizeSettings =
                this.internalResizeSettings.resizeSettingsByTemplateId &&
                Object.keys(this.internalResizeSettings.resizeSettingsByTemplateId).length > 0;

            if (this.resizeType !== ResizeType.Off) {
                const sanitisedResizeSettings = this.sanitiseResizeSettings(this.internalResizeSettings);
                this.$emit("setResizeSettings", sanitisedResizeSettings);
            } else if (hasTemplateSpecificResizeSettings) {
                const sanitisedResizeSettings = this.sanitiseResizeSettings({
                    resizeSettingsByTemplateId: this.internalResizeSettings.resizeSettingsByTemplateId
                });
                this.$emit("setResizeSettings", sanitisedResizeSettings);
            } else {
                this.$emit("setResizeSettings", undefined);
            }

            this.close();
        },

        close() {
            this.$emit("close");
        },
        removeTemplateResizeSettings(templateId: string) {
            if (this.internalResizeSettings.resizeSettingsByTemplateId) {
                delete this.internalResizeSettings.resizeSettingsByTemplateId[templateId];
            }
        },

        setTemplateResizeSettings(templateId: string, templateResizeSettings: ResizeSettings) {
            if (this.isSingleTemplateWithOwnMediaItem) {
                /*
                    If this is a single template with its own media item then
                    we want to set the resize settings at the top level, not
                    as part of resizeSettingsByTemplateId.
                */
                this.internalResizeSettings = {
                    ...this.internalResizeSettings,
                    ...templateResizeSettings
                };
            } else {
                if (!this.internalResizeSettings.resizeSettingsByTemplateId) {
                    this.internalResizeSettings.resizeSettingsByTemplateId = {};
                }

                this.internalResizeSettings.resizeSettingsByTemplateId[templateId] = templateResizeSettings;
            }
        },

        onInputChange(val: Dimensions) {
            this.manualDimensions = val;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.resize-settings-modal__settings-container {
    border-bottom: 1px solid $grey3;
    margin: 0 0 $spacing;
    padding: 0 0 $spacing-small;
}

.resize-settings-modal__settings-container--no-bottom-border {
    border-bottom: none;
    margin: 0;
    padding: 0;
}

.resize-settings-modal--button-spacing {
    margin: 0 0 0 $spacing-small;
}
</style>
